import React, { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { Chip } from 'vnetwork-ui'
import type { Color } from 'vnetwork-ui/dist/types'
import { useTranslation } from 'react-i18next'
import { formatDateWithFormatter } from '@/utils/function'
import { CONTRACT_PROCESS_STATUS } from '../../constants'
import type { ContractProcess } from '../../types'

interface ProcessTimelineProps {
    processes: ContractProcess[]
    selectedProcessId?: number | null
    onSelect: (process: ContractProcess) => void
}

type DotTone = {
    dot: string
    ring: string
}

const DOT_TONES: Record<string, DotTone> = {
    completed: { dot: 'bg-green-500', ring: 'ring-green-100' },
    'in-progress': { dot: 'bg-blue-500', ring: 'ring-blue-100' },
    pending: { dot: 'bg-amber-500', ring: 'ring-amber-100' },
    uploading: { dot: 'bg-cyan-500', ring: 'ring-cyan-100' },
    exchanging: { dot: 'bg-indigo-500', ring: 'ring-indigo-100' },
    cancelled: { dot: 'bg-gray-400', ring: 'ring-gray-100' },
    rejected: { dot: 'bg-gray-400', ring: 'ring-gray-100' },
}

const getDotTone = (status?: string): DotTone => {
    if (!status) return { dot: 'bg-gray-300', ring: 'ring-gray-100' }
    return DOT_TONES[status] || { dot: 'bg-gray-300', ring: 'ring-gray-100' }
}

/**
 * Vertical timeline list for Contract Processes (top-to-bottom).
 * Clicking an item should update URL param `process_id` in parent to open the right drawer.
 */
const ProcessTimeline: React.FC<ProcessTimelineProps> = ({
    processes,
    selectedProcessId,
    onSelect,
}) => {
    const { t } = useTranslation()

    const statusOptions = useMemo(() => CONTRACT_PROCESS_STATUS(t), [t])

    // Keep order stable; if API already sorts, we respect it.
    // If you want chronological order later, sort here by created_at.
    const rows = processes || []

    return (
        <div className="relative">
            {rows.map((p, index) => {
                const isLast = index === rows.length - 1
                const isSelected = selectedProcessId === p.contract_process_id
                const status = statusOptions?.find(
                    (s) => s.value === p.contract_process_status
                )
                const tone = getDotTone(p.contract_process_status)

                return (
                    <div
                        key={p.contract_process_id}
                        className="relative flex gap-3 pb-4 last:pb-0"
                    >
                        {/* Timeline line */}
                        {!isLast && (
                            <div className="absolute left-[9px] top-5 w-0.5 h-full bg-gray-200" />
                        )}

                        {/* Timeline dot */}
                        <div
                            className={cn(
                                'relative z-10 mt-1.5 flex-shrink-0 w-5 h-5 rounded-full border-2 border-white shadow-sm ring-4',
                                tone.dot,
                                tone.ring
                            )}
                            title={String(p.contract_process_status || '')}
                        />

                        {/* Content card */}
                        <button
                            type="button"
                            onClick={() => onSelect(p)}
                            className={cn(
                                'flex-1 text-left rounded-md border bg-white transition-colors hover:bg-gray-50/60',
                                isSelected
                                    ? 'border-primary/40 ring-2 ring-primary/10'
                                    : 'border-gray-200'
                            )}
                        >
                            <div className="px-3 py-2.5">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="text-xs text-gray-400 shrink-0">
                                                #{p.contract_process_id}
                                            </span>
                                            <span className="text-sm font-medium text-gray-800 truncate">
                                                {p.contract_process_title ||
                                                    '---'}
                                            </span>
                                            {status && (
                                                <Chip
                                                    label={
                                                        status.label ||
                                                        (p.contract_process_status as string)
                                                    }
                                                    color={
                                                        status.color as Color
                                                    }
                                                    size="small"
                                                />
                                            )}
                                        </div>

                                        <div className="mt-1 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
                                            <div className="min-w-0">
                                                <span className="text-gray-400">
                                                    {t('creator')}:{' '}
                                                </span>
                                                <span className="text-gray-700">
                                                    {p.employee_name || '---'}
                                                </span>
                                            </div>

                                            {p.employee_names?.length ? (
                                                <div className="min-w-0">
                                                    <span className="text-gray-400">
                                                        {t('assignees')}:{' '}
                                                    </span>
                                                    <span
                                                        className="text-gray-700 truncate"
                                                        title={p.employee_names.join(
                                                            ', '
                                                        )}
                                                    >
                                                        {p.employee_names.join(
                                                            ', '
                                                        )}
                                                    </span>
                                                </div>
                                            ) : null}

                                            {p.contract_process_deadline ? (
                                                <div className="min-w-0">
                                                    <span className="text-gray-400">
                                                        {t(
                                                            'contract_process_deadline'
                                                        )}
                                                        :{' '}
                                                    </span>
                                                    <span className="text-orange-700">
                                                        {formatDateWithFormatter(
                                                            p.contract_process_deadline,
                                                            'DD/MM/YYYY'
                                                        )}
                                                    </span>
                                                </div>
                                            ) : null}
                                        </div>

                                        {!!p.contract_process_problem && (
                                            <div className="mt-2 text-xs">
                                                <span className="inline-flex items-center rounded bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5">
                                                    {t(
                                                        'contract_process_problem'
                                                    )}
                                                </span>
                                                <span className="ml-2 text-gray-600">
                                                    {p.contract_process_problem}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="shrink-0 text-right">
                                        <div className="text-xs text-gray-400">
                                            {formatDateWithFormatter(
                                                p.created_at,
                                                'DD/MM/YYYY'
                                            )}
                                        </div>
                                        <div className="text-[11px] text-gray-400">
                                            {t('view')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </button>
                    </div>
                )
            })}
        </div>
    )
}

export default ProcessTimeline
